import { MutableRefObject, useState, useEffect } from "react";
import { SecurityTypeCode, SecurityTradeType } from "api/holdings/types";
import { useGetPortfolioHoldingFromPfReport } from "api/holdings/useGetPortfolioHoldingFromPfReport";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { BankAccount, useGetBuyData } from "api/trading/useGetBuyData";
import { ExecutionMethod, useTrade } from "api/trading/useTrade";
import {
  PortfolioSelect,
  DownloadableDocument,
  Button,
  Input,
  LabeledDiv,
} from "components/index";
import { Option, Select } from "components/Select/Select";
import { BANK_APPROVED } from "config";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { round } from "utils/number";
import { addProtocolToUrl } from "utils/url";
import { useGetSecurityDetails } from "../../../api/holdings/useGetSecurityDetails";
import { useTradablePortfolioSelect } from "../useTradablePortfolioSelect";
import { useTradeAmountInput, MAX_AMOUNT_SOLD } from "./useTradeAmountInput";

export interface SellModalInitialData {
  id: number;
}

export interface BankAccountOption extends Option {
  value: BankAccount;
}

interface SellModalProps extends SellModalInitialData {
  modalInitialFocusRef: MutableRefObject<null>;
  onClose: () => void;
}

const isSecurityTypeFund = (securityType: SecurityTypeCode | undefined) =>
  securityType === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE || securityType === SecurityTypeCode.FUND;


const getTradeType = (
  securityType: SecurityTypeCode | undefined,
  isTradeInUnits: boolean | undefined
) =>
  !isSecurityTypeFund(securityType)
    ? "sell"
    : isTradeInUnits
    ? "redemption_units"
    : "redemption_cash";

const getCurrentAmount = (
  isTradeInUnits: boolean | undefined,
  amount: number,
  marketValue: number,
  marketFxRate: number,
  netSoldCash: number,
  netSoldAmount: number,
) =>
  isTradeInUnits
    ? amount + Math.min(netSoldAmount, 0)
    : round((marketValue + Math.min(netSoldCash, 0)) * marketFxRate, 2);

const getTradeAmount = (
  isTradeInUnits: boolean | undefined,
  tradeAmount: number,
  price: number
) => (isTradeInUnits ? tradeAmount * price : tradeAmount);

const getTradeAmountArgs = (
  amount: number,
  isTradeInUnits: boolean | undefined
) =>
  isTradeInUnits ? { units: amount } : { amount: amount, tradeAmount: amount };

export const SellModalContent = ({
  modalInitialFocusRef,
  onClose,
  id: securityId,
}: SellModalProps) => {
  const {
    data: security = {
      name: "",
      url2: undefined,
      type: { code: undefined },
      latestMarketData: undefined,
      fxRate: 1,
      securityCode: "",
      isinCode: "",
      currency: { securityCode: "" },
      tagsAsSet: [],
      profileDataString: "",
    },
  } = useGetSecurityDetails(securityId.toString());
  const {
    name: securityName,
    currency: { securityCode: currency },
    url2,
    type: { code: securityType } = {},
    latestMarketData,
    tagsAsSet: securityTags,
    profileDataString,
  } = security;

  // Getting Max amount sold starts here
  const profileDataList = profileDataString.split(",\n");

  const profileData = profileDataList.map((item) => item.split("="));

  const allProfiles = Object.fromEntries(profileData);

  const maxPercentSold =
    allProfiles?.["security.tradeinfo.maxSell"] / 100 || MAX_AMOUNT_SOLD;

  const price = ((data) => (data ? data.price : 0))(latestMarketData);

  const [isTradeInUnits, setIsTradeInUnits] = useState(true);
  const [canToggleTradeType, setCanToggleTradeType] = useState(false);
  const unitPrice = isTradeInUnits ? price.toString() : "1";
  //in case it takes some time to loop through tags and deduct trade type
  const [hasDeductedTradeType, setHasDeductedTradeType] = useState(false);
  useEffect(() => {
    setHasDeductedTradeType(false);
    const isTradeTypeSpecified = securityTags?.some(
      (tag) =>
        tag === SecurityTradeType.sellUnits ||
        tag === SecurityTradeType.sellTradeAmount
    );
    const isUnitsSupported = securityTags?.some(
      (tag) => tag === SecurityTradeType.sellUnits
    );
    const isTradeAmountSupported = securityTags?.some(
      (tag) => tag === SecurityTradeType.sellTradeAmount
    );
    const isUnitsDefaultTradeType = true; //always true when selling
    setCanToggleTradeType(
      isTradeTypeSpecified && isUnitsSupported && isTradeAmountSupported
    );
    setIsTradeInUnits(
      isTradeTypeSpecified ? isUnitsSupported : isUnitsDefaultTradeType
    );
    setHasDeductedTradeType(true);
  }, [securityTags, securityType]);

  const { t } = useModifiedTranslation();
  const { selectedContactId } = useGetContactIdData();
  const { data: { portfolios } = { portfolios: [] } } = useGetContactInfo(
    false,
    selectedContactId
  );
  const { portfolioId, setPortfolioId, portfolioOptions } =
    useTradablePortfolioSelect();

  const selectedPortfolioId = portfolioId;
  const {
    data: {
      marketValue = 0,
      marketFxRate = 1,
      amount: units = 0,
    } = {},
    netSoldCash = 0,
    netSoldAmount = 0
  } = useGetPortfolioHoldingFromPfReport(
    selectedPortfolioId,
    securityId.toString()
  );

  const currentAmount = getCurrentAmount(
    isTradeInUnits,
    units,
    marketValue,
    marketFxRate,
    netSoldCash,
    netSoldAmount
  );

  const {
    inputValue,
    setInputState,
    inputMode,
    isTradeAmountCorrect,
    amount,
    setTradeAmountToAll,
    setTradeAmountToHalf,
  } = useTradeAmountInput(currentAmount, currency, isTradeInUnits);

  const { loading, data: { bankAccounts: accountList = [] } = {} } =
    useGetBuyData(selectedPortfolioId);

  const getBankAccounts = (
    allAccounts: BankAccount[] = accountList
  ): BankAccount[] => {
    if (allAccounts.length === 0) {
      return [];
    } else {
      return allAccounts
        .filter(
          (e) =>
            e.cashAccount === true ||
            e.tags.toLowerCase().includes(BANK_APPROVED.toLowerCase())
        )
        .map((bank) => ({
          ...bank,
          checkAccountAmount: bank.cashAccount ? true : false,
        }));
    }
  };

  const bankAccountList = getBankAccounts();
  const [selectedCashAccount, setSelectedCashAccount] = useState<
    BankAccountOption | undefined
  >(undefined);

  const { handleTrade: handleSell, submitting } = useTrade({
    tradeType: getTradeType(securityType, isTradeInUnits),
    portfolio:
      portfolios.find((portfolio) => portfolio.id === portfolioId) ||
      portfolios[0],
    securityName,
    ...getTradeAmountArgs(amount, isTradeInUnits),
    ...security,
    currency,
    executionMethod: isTradeInUnits
      ? ExecutionMethod.UNITS
      : ExecutionMethod.NET_TRADE_AMOUNT,
    unitPrice,
    account: selectedCashAccount?.value?.number || "",
  });

  const { readonly } = useKeycloak();

  const expectedTradeAmount = getTradeAmount(isTradeInUnits, amount, price);

  return (
    <div className="grid gap-2 min-w-[min(84vw,_375px)]">
      <LabeledDiv
        label={t("tradingModal.securityName")}
        className="text-2xl font-semibold"
      >
        {securityName}
      </LabeledDiv>
      <LabeledDiv
        label={t("tradingModal.isinCode")}
        className="text-xl font-semibold"
      >
        {security.isinCode}
      </LabeledDiv>
      {url2 && (
        <div className="w-fit">
          <DownloadableDocument
            url={addProtocolToUrl(url2)}
            label={t("tradingModal.kiid")}
          />
        </div>
      )}
      <PortfolioSelect
        portfolioOptions={portfolioOptions}
        portfolioId={portfolioId}
        onChange={(newPortfolio) => setPortfolioId(newPortfolio.id)}
        label={t("tradingModal.portfolio")}
      />
      <Select
        value={selectedCashAccount}
        options={bankAccountList.map((account) => {
          return {
            id: account.id,
            value: account,
            label: account.number,
            OptionComponent: <div>{`${account.name} - ${account.number}`}</div>,
          };
        })}
        onChange={(option) => setSelectedCashAccount(option)}
        label={t("tradingModal.bankAccount")}
      ></Select>

      {portfolioId && !loading && !bankAccountList.length ? (
        <div className="max-w-sm">{t("tradingModal.noBankAccounts")}</div>
      ) : (
        ""
      )}
      {isTradeInUnits ? (
        <LabeledDiv
          label={t("tradingModal.currentUnits")}
          className="text-xl font-semibold text-gray-700"
        >
          {currency &&
            t("number", {
              value: currentAmount,
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            })}
        </LabeledDiv>
      ) : (
        <LabeledDiv
          label={t("tradingModal.currentMarketValue")}
          className="text-xl font-semibold text-gray-700"
        >
          {currency &&
            t("numberWithCurrency", {
              value: currentAmount,
              currency: currency,
            })}
        </LabeledDiv>
      )}
      <Input
        disabled={!hasDeductedTradeType || !portfolioId}
        ref={modalInitialFocusRef}
        value={inputValue || ""}
        onChange={(event) => {
          setInputState((previousState) => {
            const target = event.target as HTMLInputElement;
            return {
              ...previousState,
              inputValue: Number(target?.value || 0),
            };
          });
        }}
        label={
          isTradeInUnits
            ? t("tradingModal.unitsInputLabel")
            : t("tradingModal.tradeAmountInputLabel", {
                currency: inputMode.label,
              })
        }
        type="number"
        error={
          !isTradeAmountCorrect && !loading
            ? !isTradeInUnits &&
              expectedTradeAmount < marketValue &&
              expectedTradeAmount > marketValue * maxPercentSold
              ? t("tradingModal.tradeAmountInputErrorAmount", {
                  maxValue: maxPercentSold * 100,
                })
              : t("tradingModal.tradeAmountInputError")
            : undefined
        }
      />

      {canToggleTradeType && (
        <>
          <div className="flex overflow-hidden font-medium leading-5 bg-gray-50 rounded-md divide-x ring-1 shadow-sm pointer-events-auto select-none divide-slate-400/20 text-[0.8125rem] ring-slate-700/10">
            <button
              className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                isTradeInUnits ? "bg-gray-200" : ""
              }`}
              onClick={() => setIsTradeInUnits(true)}
            >
              {t("tradingModal.unitsButtonLabel")}
            </button>

            <button
              className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                !isTradeInUnits ? "bg-gray-200" : ""
              }`}
              onClick={() => setIsTradeInUnits(false)}
            >
              {t("tradingModal.tradeAmountButtonLabel")}
            </button>
          </div>
        </>
      )}

      <div className="flex justify-between items-center h-8">
        <div className="flex gap-1 items-center">
          {isTradeInUnits && (
            <Button size="xs" variant="Secondary" onClick={setTradeAmountToAll}>
              {t("tradingModal.sellAll")}
            </Button>
          )}
          {isTradeInUnits && (
            <Button
              size="xs"
              variant="Secondary"
              onClick={setTradeAmountToHalf}
            >
              {t("tradingModal.sellHalf")}
            </Button>
          )}
        </div>
      </div>

      <hr />
      <div className="flex flex-col gap-4 items-stretch ">
        <div className="text-3xl font-semibold text-center">
          <div className="text-base font-normal">
            {t("tradingModal.tradeAmount")}
          </div>
          {t("numberWithCurrency", {
            value: isTradeAmountCorrect
              ? getTradeAmount(isTradeInUnits, amount, price)
              : 0,
            currency,
          })}
        </div>
        <Button
          disabled={
            readonly ||
            amount === 0 ||
            loading ||
            !isTradeAmountCorrect ||
            !portfolioId ||
            !selectedCashAccount
          }
          isLoading={submitting}
          onClick={async () => {
            const response = await handleSell();
            if (response) {
              onClose();
            }
          }}
        >
          {t("tradingModal.sellModalHeader")}
        </Button>
      </div>
    </div>
  );
};
